import React from "react";
import { DeleteIcon } from "../../../assets/icons/index";

interface ISellableProductAssociationItem {
  id: number;
  name: string;
  isHandScorable?: boolean;
}

interface ISellableProductAssociationTableProps {
  items: ISellableProductAssociationItem[];
  idLabel: string;
  nameLabel: string;
  onDelete: (id: number) => void;
  onRowClick?: (id: number) => void;
  getEditPath?: (id: number) => string;
  showHandScorable?: boolean;
  onToggleHandScorable?: (id: number, checked: boolean) => void;
}

export const SellableProductAssociationTable = ({
  items,
  idLabel,
  nameLabel,
  onDelete,
  onRowClick,
  getEditPath,
  showHandScorable,
  onToggleHandScorable,
}: ISellableProductAssociationTableProps) => {
  const linkable = !!getEditPath;
  const linkCellClasses = linkable
    ? " cursor-pointer text-indigo-600 hover:text-indigo-500"
    : "";

  const renderCell = (id: number, content: React.ReactNode) =>
    linkable && getEditPath ? <a href={getEditPath(id)}>{content}</a> : content;

  return (
    <table className="min-w-full divide-y divide-gray-200">
      <thead className="bg-primary-color">
        <tr>
          <th
            scope="col"
            className="inline-flex items-center gap-2 px-3 py-2 font-light text-sm text-left text-white dark:text-white tracking-wider"
          >
            <span className="flex gap-2">{idLabel}</span>
          </th>
          <th
            scope="col"
            className="px-3 py-2  font-light text-sm text-left text-white dark:text-white tracking-wider"
          >
            <span className="flex gap-2">{nameLabel}</span>
          </th>
          {showHandScorable && (
            <th
              scope="col"
              className="px-3 py-2 font-light text-sm text-center text-white dark:text-white tracking-wider"
            >
              Hand-Scorable
            </th>
          )}
          <th
            scope="col"
            className="px-3 py-2 font-light text-sm text-center text-white dark:text-white tracking-wider"
          >
            Action
          </th>
        </tr>
      </thead>
      <tbody>
        {items.map((item, index) => (
          <tr
            key={item.id}
            className={index % 2 === 0 ? "bg-white" : "bg-gray-100"}
          >
            <td
              className={`px-3 py-2 whitespace-nowrap text-sm text-left font-normal text-gray-500${linkCellClasses}`}
              onClick={
                linkable && onRowClick ? () => onRowClick(item.id) : undefined
              }
            >
              {renderCell(item.id, item.id)}
            </td>
            <td
              className={`px-3 py-2 pl-6 whitespace-nowrap text-sm text-left font-normal text-gray-500${linkCellClasses}`}
              onClick={
                linkable && onRowClick ? () => onRowClick(item.id) : undefined
              }
            >
              {renderCell(item.id, item.name)}
            </td>
            {showHandScorable && (
              <td className="px-3 py-2 whitespace-nowrap text-sm text-center font-normal text-gray-500">
                <input
                  type="checkbox"
                  checked={!!item.isHandScorable}
                  className="focus:ring-primary-color h-4 w-4 text-primary-color border-gray-300 rounded"
                  onChange={(e) =>
                    onToggleHandScorable?.(item.id, e.target.checked)
                  }
                />
              </td>
            )}
            <td className="px-3 py-2 whitespace-nowrap text-sm text-center font-normal text-gray-700">
              <div className="flex gap-3 justify-center">
                <DeleteIcon
                  onClick={() => onDelete(item.id)}
                  className="w-4 text-xs font-light cursor-pointer hover:text-red-500 transaction ease-in-out duration-300"
                />
              </div>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};
